import { NetbankingAPI } from "aaa";
import { action, computed, observable } from "mobx";
import { NetbankingAPIClient } from "network/APIClient";
import { IContact, IPostContactResponse } from "types/contact";

export function phoneNumberToString(contact: IContact) {
    if (contact.phone) {
        return `${contact.phone.countryCallingCode ?? ""} ${contact.phone.areaCallingCode ?? ""} ${contact.phone.phoneNumber}`;
    }

    return "";
}

export function addressToString(address: IContact) {
    if (address.address) {
        return `${address.address!.street || ""}, ${address.address!.zipCode || ""} ${address.address!.city || ""}, ${address.address!.country || ""}`;
    } else {
        return "";
    }
}

function findMainContact(contacts: IContact[]): IContact | undefined {
    let mainContact = contacts.find(contact => {
        return contact.flags && contact.flags.indexOf("mainContact") >= 0;
    });

    if (!mainContact) {
        mainContact = contacts[0];
    }

    return mainContact;
}

const validContactsFilter = (contact: IContact) => contact.flags && !contact.flags.includes("invalidContact");

export class ContactsStore {
    @observable contacts: IContact[] = [];

    @observable emailAddresses: IContact[] = [];
    @observable selectedEmailAddressId?: string;
    @computed get selectedEmailAddress(): IContact | undefined {
        return this.emailAddresses.find(contact => contact.id === this.selectedEmailAddressId);
    }

    @observable mainAddressOnly = false;
    @observable permanentResidenceOnly = false;
    @observable addresses: IContact[] = [];
    @observable primaryAddress?: IContact;
    @observable selectedAddressId?: string;
    @computed get selectedAddress(): IContact | undefined {
        return this.addresses.find(contact => contact.id === this.selectedAddressId);
    }

    @observable invalidPrimaryAddress?: IContact;

    @observable mobilePhones: IContact[] = [];
    @observable watchdogActive = false;
    @observable selectedPhoneId?: string;
    @computed get selectedPhone(): IContact | undefined {
        return this.mobilePhones.find(contact => contact.id === this.selectedPhoneId);
    }

    @observable isLoading: boolean = false;

    private static filter(
        contacts: IContact[],
        type: "EMAIL" | "ADDRESS" | "PHONE",
        flag?: string
    ): IContact[] {
        if (!contacts) {
            return [];
        }

        return contacts.filter(entry => {
            const typeMatches = entry.type === type;
            let flagMatches = true;
            if (flag !== undefined) {
                flagMatches = false;
                let contact: any;
                switch (type) {
                    case "EMAIL":
                        contact = entry.email;
                        break;
                    case "ADDRESS":
                        contact = entry.address;
                        break;
                    case "PHONE":
                        contact = entry.phone;
                        break;
                    default:
                        break;
                }

                if (contact && contact.flags) {
                    flagMatches = contact.flags.indexOf(flag) >= 0;
                }
            }

            return typeMatches && flagMatches;
        });
    }

    constructor(mainAddressOnly?: boolean, permanentResidenceOnly?: boolean) {
        this.mainAddressOnly = !!mainAddressOnly;
        this.permanentResidenceOnly = !!permanentResidenceOnly;
    }

    @action async load(addressFilter?: string, addressCountry?: string) {
        const target = NetbankingAPI.getContacts();
        this.isLoading = true;

        try {
            this.contacts = await NetbankingAPIClient.requestType(target);
            const addresses = addressFilter
                ? (await NetbankingAPIClient.requestType(NetbankingAPI.getContacts(addressFilter)))
                : this.contacts;
            this.isLoading = false;

            this.findEmailAddresses();
            this.findMobilePhones();
            this.findPrimaryAddress(addresses, addressCountry);

        } catch (error) {
            this.isLoading = false;
            throw error;
        }
    }

    @action async addEmail(email: string, type: "PRIVATE" | "COMPANY", mainContact: boolean): Promise<IPostContactResponse | undefined> {
        const target = NetbankingAPI.postEmail(email, type, mainContact);
        return NetbankingAPIClient.requestType(target);
    }

    @action async addPhone(
        countryCallingCode: string,
        areaCallingCode: string,
        phoneNumber: string,
        weekdays: boolean,
        saturday: boolean,
        sundayHolidays: boolean,
        type: "PRIVATE" | "COMPANY",
        mobile: boolean,
        mainContact: boolean): Promise<IPostContactResponse | undefined> {
        const target = NetbankingAPI.postPhone(countryCallingCode, areaCallingCode, phoneNumber, weekdays, saturday, sundayHolidays, type, mobile, mainContact);

        try {
            this.isLoading = true;
            const response = await NetbankingAPIClient.requestType(target);
            this.isLoading = false;
            return response;
        } catch (error) {
            this.isLoading = false;
            throw error;
        }
    }

    @action async addAddress(
        street: string,
        streetNumber: string,
        zipCode: string,
        city: string,
        country: string,
        mainContact: boolean
    ): Promise<IPostContactResponse | undefined> {
        const target = NetbankingAPI.postAddress(
            street,
            streetNumber,
            zipCode,
            city,
            country,
            mainContact
        );

        try {
            this.isLoading = true;
            const response = await NetbankingAPIClient.requestType(target);
            this.isLoading = false;
            return response;
        } catch (error) {
            this.isLoading = false;
            throw error;
        }
    }

    @action async updateAddress(
        address: IContact,
        street: string,
        streetNumber: string,
        zipCode: string,
        city: string,
        country: string,
        mainContact: boolean
    ): Promise<IPostContactResponse | undefined> {
        const target = NetbankingAPI.putAddress(
            address,
            street,
            streetNumber,
            zipCode,
            city,
            country,
            mainContact
        );

        try {
            this.isLoading = true;
            const response = await NetbankingAPIClient.requestType(target);
            this.isLoading = false;
            return response;
        } catch (error) {
            this.isLoading = false;
            throw error;
        }
    }

    private findPrimaryAddress(contacts: IContact[], addressCountry?: string) {
        this.addresses = ContactsStore.filter(contacts, "ADDRESS")
            .filter(contact => this.permanentResidenceOnly ? contact.address?.type === "PERMANENT_RESIDENCE" : true)
            .filter(validContactsFilter)
            .filter(contact => addressCountry ? contact.address?.country === addressCountry : true)
            .filter(contact => this.mainAddressOnly ? (contact.flags && contact.flags.includes("mainContact")) : true);

        if (this.addresses.length > 0) {
            this.primaryAddress = findMainContact(this.addresses);
            this.selectedAddressId = this.primaryAddress?.id;
        } else if (this.mainAddressOnly) {
            const invalidAddresses = ContactsStore.filter(contacts, "ADDRESS")
                .filter(contact => this.permanentResidenceOnly ? contact.address?.type === "PERMANENT_RESIDENCE" : true)
                .filter(contact => !validContactsFilter(contact))
                .filter(contact => contact.flags && contact.flags.includes("mainContact"));

            this.invalidPrimaryAddress = invalidAddresses.find(contact => (contact.flags && contact.flags.includes("mainContact")));
        }

    }

    private findEmailAddresses() {
        this.emailAddresses = ContactsStore.filter(this.contacts, "EMAIL").filter(validContactsFilter);
        if (this.emailAddresses.length > 0) {
            const main = findMainContact(this.emailAddresses);
            this.selectedEmailAddressId = main?.id;
        }
    }

    private findMobilePhones() {
        this.mobilePhones = ContactsStore.filter(this.contacts, "PHONE", "mobile").filter(validContactsFilter);
        if (this.mobilePhones.length > 0) {
            const main = findMainContact(this.mobilePhones);
            this.selectedPhoneId = main?.id;
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/ContactsStore.ts